import { ChangeEvent, FormEvent, FunctionComponent, ReactElement, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Typography, Box, Link } from '@mui/material';
import { isValidEmail, requiredField } from '@/shared/utils/validation';
import { INVALID_EMAIL, WRONG_EMAIL } from '@/shared/utils/validation/constants';
import LoadingButton from '@/components/ui/loading-button';
import { Stack } from '@mui/system';
import { useSendPasswordResetEmail } from '@/queries/auth';
import TextField from '@/components/ui/form-inputs/TextField';
import { FORGOT_PASSWORD_FORM_KEY } from '@/shared/constants/form-keys';
import InstructionSent from './InstructionSent';
import AuthCardLayout from '../../layouts/auth-card-layout/AuthCardLayout';
import AuthLayout from '../../layouts/auth-layout/AuthLayout';

const ForgotPasswordForm: FunctionComponent = (): ReactElement => {
	const [email, setEmail] = useState('');
	const [emailSent, setEmailSent] = useState(false);

	const [emailError, setEmailError] = useState('');

	const { isLoading, mutate: sendPasswordResetEmail } = useSendPasswordResetEmail({
		onSuccess: () => setEmailSent(true),
		onError: () => setEmailError(WRONG_EMAIL),
	});

	const validateEmail = (): boolean => {
		if (email.length === 0) {
			setEmailError(requiredField('Email'));
			return false;
		}

		if (!isValidEmail(email)) {
			setEmailError(INVALID_EMAIL);
			return false;
		}

		setEmailError('');
		return true;
	};

	const sendPasswordResetEmailAction = (event: FormEvent) => {
		event.preventDefault();
		if (!validateEmail()) {
			return;
		}

		sendPasswordResetEmail({
			request: {
				request: { email },
				formKey: FORGOT_PASSWORD_FORM_KEY,
			},
		});
	};

	return (
		<Box component="section" role="main">
			<AuthLayout>
				{emailSent ? (
					<InstructionSent email={email} />
				) : (
					<AuthCardLayout title="Forgot password">
						<Box component="form" onSubmit={sendPasswordResetEmailAction} mt={3}>
							<Stack direction="column" spacing={3}>
								<Stack direction="column" spacing={2}>
									<Typography variant="body1" color="text.secondary">
										Having trouble signing in? Please enter your email to get started.
									</Typography>
									<TextField
										id="email"
										name="email"
										label="Email"
										type="email"
										formKey={FORGOT_PASSWORD_FORM_KEY}
										fullWidth
										required
										value={email}
										onChange={(event: ChangeEvent<HTMLInputElement>) =>
											setEmail(event.target.value)
										}
										clientErrors={emailError}
									/>
									<Link component={RouterLink} to="/auth/login">
										Back to log in
									</Link>
								</Stack>

								<LoadingButton
									loading={isLoading}
									type="submit"
									variant="contained"
									size="large"
									fullWidth
								>
									Submit
								</LoadingButton>
							</Stack>
						</Box>
					</AuthCardLayout>
				)}
			</AuthLayout>
		</Box>
	);
};

export default ForgotPasswordForm;
